import { createTheme, type Theme } from "@mui/material/styles";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { color, sidebar, workspaceAccent } from "./tokens/color.js";
import { typography } from "./tokens/typography.js";
import { spacing } from "./tokens/spacing.js";
import { radius, cornerAccent } from "./tokens/radius.js";
import { density, DENSITY_ATTR, type DensityMode } from "./tokens/density.js";
import { elevation } from "./tokens/elevation.js";
import { shadow } from "./tokens/shadow.js";
import { motion } from "./tokens/motion.js";
import { zIndex } from "./tokens/zIndex.js";
import { container } from "./tokens/container.js";
import { chrome } from "./tokens/chrome.js";
import { breakpoints } from "./tokens/breakpoints.js";
// TRIAL — see `tokens/trialPalette.ts`. Exported so the console's filter chips
// can consume the handoff's chip tokens; goes away with the trial.
import { trialChip, trialFreshnessDot } from "./tokens/trialPalette.js";
import { trialTonalButtonSx } from "./trialControls.js";

// SB6-ANA-UI-01 — shared compact-control sizing presets (the global "dense
// sizes" home for all apps). Re-exported here next to the token files.
export * from "./compact.js";

// D81's chip recipe. Exported from the theme rather than a primitive because
// six feature files need it directly — they render domain states as chips and
// each had its own copy of the same colour-band switch.
export * from "./statusChip.js";

// D92 — the provider colour of a workflow node (the Workflows console's board).
export * from "./providerStyle.js";

export {
  color,
  sidebar,
  workspaceAccent,
  typography,
  spacing,
  radius,
  cornerAccent,
  density,
  DENSITY_ATTR,
  elevation,
  shadow,
  motion,
  zIndex,
  container,
  chrome,
  breakpoints,
  trialChip,
  trialFreshnessDot,
  trialTonalButtonSx,
};

export type { DensityMode };
export type { ChromeTokens } from "./tokens/chrome.js";

export function buildTheme(mode: "light" | "dark"): Theme {
  const palette = mode === "dark" ? color.dark : color.light;
  return createTheme({
    palette: {
      mode,
      // D78 — the accent across every Aerios surface is aqua.
      //
      // `contrastText` is the consequence D78 calls out as "not a token swap":
      // white on `--aqua-400` measures 1.76:1 and fails outright, while
      // `--ink-900` on it measures 9.91:1. So a filled primary control INVERTS
      // its label between modes rather than merely changing hue — dark label on
      // dark mode's brighter aqua, light label on light mode's deeper aqua.
      // It looks wrong in a diff and is right on screen.
      primary: {
        main: palette.primary,
        dark: palette.accentActive,
        light: palette.accent,
        contrastText: palette.text.inverse,
      },
      secondary: { main: palette.accent },
      // D81's slots drive MUI status icons and <Alert severity="…">. `warning`
      // maps to `attention` ("needs a human") and `error` to `critical`
      // ("failed · blocking") — MUI's names, the system's meanings.
      success: { main: palette.status.success },
      info: { main: palette.status.info },
      warning: { main: palette.status.attention },
      error: { main: palette.status.critical },
      background: {
        default: palette.surface.default,
        paper: palette.surface.paper,
      },
      text: {
        primary: palette.text.primary,
        secondary: palette.text.secondary,
        disabled: palette.text.disabled,
      },
      divider: palette.border,
    },
    typography: {
      fontFamily: typography.fontFamily,
      fontSize: typography.size.md,
      // D7 — weights 400/500/600 only. No 700, no italics.
      // Negative tracking pulls a headline into one statement rather than loose
      // words, and applies to DISPLAY AND HEADING sizes only. The label style
      // goes the other way at +0.14em; see `tracking.label-md`.
      h1: {
        fontSize: typography.size["display-md"],
        lineHeight: typography.lineHeight.tight,
        fontWeight: typography.weight.semibold,
        letterSpacing: typography.tracking["display-md"],
      },
      h2: {
        fontSize: typography.size["display-sm"],
        fontWeight: typography.weight.semibold,
        letterSpacing: typography.tracking["display-sm"],
      },
      h3: {
        fontSize: typography.size["title-lg"],
        fontWeight: typography.weight.semibold,
        letterSpacing: typography.tracking["title-lg"],
      },
      h4: {
        fontSize: typography.size["title-md"],
        fontWeight: typography.weight.semibold,
        letterSpacing: typography.tracking["title-md"],
      },
      h5: {
        fontSize: typography.size["body-md"],
        fontWeight: typography.weight.semibold,
        letterSpacing: typography.tracking["title-md"],
      },
      body1: {
        fontSize: typography.size["body-md"],
        lineHeight: typography.lineHeight.base,
      },
      body2: { fontSize: typography.size["body-sm"] },
      // Sentence case, never UPPERCASE — and NOT the label tracking, which is
      // for 11px mono caps. A sentence-case button with +0.14em would read as
      // spaced-out text rather than a control.
      button: {
        textTransform: "none",
        fontWeight: typography.weight.medium,
        letterSpacing: "0em",
      },
      /**
       * `overline` is MUI's slot for a small caps label, so the system's label
       * style lands here: 11px mono caps at +0.14em (D6/D77). It does not grow
       * with density and must never outrank the value it introduces.
       */
      overline: {
        fontFamily: typography.fontFamilyMono,
        fontSize: typography.size["label-md"],
        fontWeight: typography.weight.medium,
        letterSpacing: typography.tracking["label-md"],
        textTransform: "uppercase",
        lineHeight: 1.4,
      },
    },
    // D72 — controls land on 12. Cards override to `radius.card` below.
    shape: { borderRadius: radius.control },
    transitions: {
      duration: {
        shortest: motion.duration.fast,
        standard: motion.duration.base,
        complex: motion.duration.slow,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.control,
            // P2 — structure is drawn, not shaded. A button is not floating
            // above anything, so it throws no shadow in either system.
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          },
          // D77 — comfortable is the default and needs no attribute; compact is
          // opted into on a REGION and inherited.
          //
          // This reads the design system's OWN density contract rather than
          // reimplementing it: `tokens.css` defines `--control-h: 44px` on
          // `:root` and `32px` under `[data-density="compact"]`, so the cascade
          // already carries the inheritance. The theme only has to consume it.
          //
          // The first attempt did reimplement it, as a `[data-density="compact"] &`
          // descendant selector per variant. It worked, and it made jsdom match
          // that selector for every button on a long list — enough to tip a
          // 3.9s DefectsPanel spec over its 5s timeout, twice, reproducibly. A
          // custom property is one lookup instead of a tree walk, and it is the
          // mechanism the system shipped for exactly this.
          //
          // The fallback matters: `packages/ui` renders in tests without the
          // app stylesheet, and a surface that never opts in must still get the
          // comfortable default.
          contained: {
            paddingInline: `var(--control-px, ${String(density.comfortable.controlPaddingX)}px)`,
            minHeight: `var(--control-h, ${String(density.comfortable.controlHeight)}px)`,
          },
          outlined: {
            minHeight: `var(--control-h, ${String(density.comfortable.controlHeight)}px)`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: radius.card,
            // D12/D74 — a card sits ON the page, it does not float above
            // scrolling content, so it is DRAWN rather than shadowed. Shadow is
            // reserved for surfaces that genuinely float, and even then it is
            // additive on top of a drawn edge, never the only thing separating
            // the surface from the page.
            boxShadow: "none",
            border: `1px solid ${palette.borders.subtle}`,
            backgroundImage: "none",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
          rounded: { borderRadius: radius.card },
        },
      },
      // D77, applied to FIELDS as well as buttons. The theme already pins a
      // contained or outlined button to `var(--control-h)`; a field pinned
      // nothing, so `size="small"` gave MUI's own 40px — not one of the three
      // sanctioned heights, and not a number anyone chose. A filter row that
      // mixed shared primitives with hand-built fields therefore could not
      // line up no matter what the primitives did (Christian, 2026-09-23,
      // Data Acquisitions: eight controls, seven of them raw).
      //
      // Both halves are needed. `minHeight` alone cannot SHRINK a field that
      // is already 40, so the small input's vertical padding comes down to 4px
      // and the band governs in both directions:
      //
      //   comfortable  content 31px  →  min-height 44  →  44px
      //   compact      content 31px  →  min-height 32  →  32px
      //
      // Scoped to `sizeSmall`, so a standard-size field — a form's own inputs —
      // keeps MUI's geometry and is unaffected.
      MuiInputBase: {
        styleOverrides: {
          root: { minHeight: "var(--control-h, 44px)" },
        },
      },
      // The padding lives HERE, not on `MuiInputBase`, because
      // `.MuiOutlinedInput-input` sets its own `padding: 8.5px 14px` for
      // `size="small"` and beats an InputBase rule. That is exactly why the
      // first attempt measured 41px on a page whose band said 32 — the height
      // floor applied and the padding did not, so nothing lined up
      // (measured on Data Acquisitions, 2026-09-23).
      MuiOutlinedInput: {
        styleOverrides: {
          inputSizeSmall: { paddingTop: 4, paddingBottom: 4 },
          root: {
            borderRadius: radius.control,
            // WCAG 1.4.11 — the edge that IDENTIFIES the control needs 3:1.
            // `divider` is a hairline and measures far below that; this is the
            // separation `borders.control` exists for.
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: palette.borders.control,
            },
          },
        },
      },
      // ---- D85 · the elevation stack -----------------------------------
      // D12 gives hairline or one surface step; D74 extends shadow to chrome
      // that floats above scrolling content, on one condition: a floating
      // surface must ALSO be drawn. Every level below carries a hairline or a
      // surface step, with the shadow additive on top. A shadow doing the
      // structural work alone is a defect.
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: radius.card,
            border: `1px solid ${palette.borders.subtle}`,
            backgroundColor: palette.surface.raised,
            boxShadow: elevation.overlay,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: radius.card,
            border: `1px solid ${palette.borders.subtle}`,
            backgroundColor: palette.surface.raised,
            boxShadow: elevation.overlay,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: radius.card,
            border: `1px solid ${palette.borders.subtle}`,
            backgroundImage: "none",
            boxShadow: elevation.overlay,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderColor: palette.borders.subtle,
            backgroundImage: "none",
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: radius.control,
            backgroundColor: palette.surface.sunken,
            color: palette.text.primary,
            border: `1px solid ${palette.borders.subtle}`,
            fontSize: typography.size["caption-lg"],
            // A tooltip "explains why, not what" — so it gets room to say a
            // sentence rather than being squeezed into a label.
            maxWidth: 320,
          },
        },
      },
      // ---- controls -----------------------------------------------------
      MuiChip: {
        styleOverrides: {
          // D72 gives pills 999 — a sentinel meaning "fully round", not a
          // measured radius. `boxed chip` takes the control radius instead;
          // MUI's `square` variant is the closest slot for that.
          root: { borderRadius: radius.pill, fontWeight: 500 },
          filled: { border: "1px solid transparent" },
        },
      },
      MuiTabs: {
        styleOverrides: {
          // The active indicator is a 2px edge, and it is one of the few places
          // aqua is doing exactly what P3 says it is for: marking current
          // position.
          indicator: { height: 2, backgroundColor: palette.primary },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: typography.weight.medium,
            letterSpacing: "0em",
            minHeight: density.compact.controlHeight,
          },
        },
      },
      MuiSelect: {
        styleOverrides: { select: { borderRadius: radius.control } },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            borderRadius: radius.card,
            border: `1px solid ${palette.borders.subtle}`,
            boxShadow: elevation.overlay,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: radius.control,
            border: "1px solid",
            borderColor: "currentColor",
          },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            borderRadius: radius.control,
            border: `1px solid ${palette.borders.subtle}`,
            backgroundColor: palette.surface.raised,
            color: palette.text.primary,
            boxShadow: elevation.overlay,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: { track: { borderRadius: radius.pill } },
      },
      MuiTableCell: {
        // D19 — no zebra striping; hover tint is the only row feedback, and the
        // hairline does the structure.
        styleOverrides: { root: { borderColor: palette.borders.subtle } },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: palette.borders.subtle } },
      },
      // D80 — the whole Lucide adaptation, in one place.
      //
      // Lucide ships the grid, the no-fill rule and colour inheritance already
      // correct. It differs on three PRESENTATION attributes — stroke-width 2,
      // round caps, round joins — and any CSS rule outranks those, which is
      // exactly why Lucide could be adopted and Phosphor could not.
      //
      // `fill: none` is not cosmetic. `.MuiSvgIcon-root` sets
      // `fill: currentColor`, and CSS beats Lucide's `fill="none"` attribute,
      // so without this every stroked icon renders as a filled blob. The
      // `&.lucide` compound selector is what wins: two classes beat one,
      // regardless of source order.
      // D77 · the way BACK out of a dense surface.
      //
      // The vendored tokens define `:root` (44px) and `[data-density="compact"]`
      // (32px) and nothing else, so a region inside a compact surface had no
      // way to declare itself comfortable. This is the missing half, added as a
      // PLATFORM rule rather than an edit to the vendored file.
      //
      // It exists for one shape of page, and the reason is worth keeping:
      // on the eleven list pages the filter row IS the tool — the operator is
      // there to narrow a fleet, not to read a cell — so that region is
      // comfortable while the table under it stays dense (Christian,
      // 2026-09-23). A whole surface is still declared once, on a region, and
      // inherited; this just makes "comfortable" as sayable as "compact".
      MuiCssBaseline: {
        styleOverrides: {
          '[data-density="comfortable"]': {
            "--control-h": "44px",
            "--control-font": "16.5px",
            "--control-px": "24px",
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            "&.lucide": {
              fill: "none",
              strokeWidth: 1.5,
              strokeLinecap: "butt",
              strokeLinejoin: "miter",
              // …except the DOTS. Lucide draws a tittle — the dot on an `i`,
              // the one under an alert triangle — as a zero-length segment
              // (`d="M12 8h.01"`), which is visible ONLY under a round cap. A
              // butt cap on a zero-length line paints nothing at all, so
              // `Info` rendered as a bare stem inside a circle and read as a
              // power symbol rather than an information mark (Christian,
              // 2026-09-23: "there is no dot on the i"). 136 of Lucide's
              // glyphs use the trick, including Info, TriangleAlert and the
              // message-warning family.
              //
              // This is NOT a reversal of D80: the squared cap stays on every
              // real stroke, and the selector reaches only the zero-length
              // segments, which have no length for a cap style to express.
              //
              // 2026-10-25 (DI-3-S53b round 8): some glyphs draw the same
              // zero-length dot as a `<line>` instead of a path —
              // `CircleAlert` is `<line x1="12" x2="12.01" y1="16" y2="16">`
              // (also badge-alert / -info / -question-mark, cast, gamepad,
              // server, tablet) — so the exception matches those too. Same
              // rationale, still only zero-length segments.
              "& [d$='.01'], & line[x2$='.01'], & line[y2$='.01']": {
                strokeLinecap: "round",
              },
            },
          },
        },
      },
    },
  });
}

export const lightTheme = buildTheme("light");
export const darkTheme = buildTheme("dark");
